import React from 'react';
import { ButtonProps } from '../types';
import theme from '../config/theme';

// Helper function to darken a color
const darkenColor = (color: string, percent: number): string => {
  // Remove # if present
  const hex = color.replace('#', '');
  
  // Convert to RGB
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Darken by percent
  const newR = Math.max(0, Math.floor(r * (1 - percent / 100)));
  const newG = Math.max(0, Math.floor(g * (1 - percent / 100)));
  const newB = Math.max(0, Math.floor(b * (1 - percent / 100)));
  
  // Convert back to hex
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
};

// Extended Button Props for form submission
interface ExtendedButtonProps extends Omit<ButtonProps, 'onClick'> {
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  backgroundColor?: string;
  useGradient?: boolean;
}

// Button Component
const Button: React.FC<ExtendedButtonProps> = ({ 
  onClick, 
  children, 
  variant = 'primary', 
  disabled = false,
  className = '',
  type = 'button',
  backgroundColor = theme.brandColor.red,
  useGradient = false
}) => {
  const baseClass = 'btn';
  const variantClass = `btn-${variant}`;
  const disabledClass = disabled ? 'btn-disabled' : '';
  
  return (
    <button 
      type={type}
      className={`py-2 text-lg font-light rounded-lg disabled:opacity-50 disabled:cursor-not-allowed text-white transition-all duration-200 hover:shadow-lg ${baseClass} ${variantClass} ${disabledClass} ${className}`}
      style={{
        background: useGradient ? theme.brandColor.primary : backgroundColor,
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
      } as React.CSSProperties}
      onMouseEnter={(e) => {
        if (!disabled) {
          if (useGradient) {
            e.currentTarget.style.background = `linear-gradient(90deg, ${darkenColor(theme.brandColor.yellow, 10)}, ${darkenColor(theme.brandColor.red, 10)})`;
          } else {
            e.currentTarget.style.backgroundColor = darkenColor(backgroundColor, 10);
          }
          e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)';
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          if (useGradient) {
            e.currentTarget.style.background = theme.brandColor.primary;
          } else {
            e.currentTarget.style.backgroundColor = backgroundColor;
          }
          e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
        }
      }}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
};

export default Button;
